import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Upload, Loader2, CheckCircle2, AlertCircle, FileImage, X, LogOut, UserRound, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pb_reviewer_token";

const SELECT_CLASSES =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-trust";
const INPUT_CLASSES = SELECT_CLASSES + " placeholder:text-gray-400";

const STATUS_META = {
  pending_verification: { label: "In verification", cls: "bg-gold-soft text-yellow-700" },
  published: { label: "Live", cls: "bg-verified-soft text-verified" },
  rejected: { label: "Not published", cls: "bg-warn-soft text-warn" },
};

const FilePicker = ({ label, hint, file, onChange, testId }) => {
  const ref = useRef(null);
  return (
    <div>
      <p className="text-sm font-semibold mb-2">
        {label} <span className="text-warn">*</span>
      </p>
      <button
        type="button"
        data-testid={testId}
        onClick={() => ref.current?.click()}
        className="w-full rounded-xl border-2 border-dashed border-gray-300 bg-paper px-4 py-5 text-left transition-colors duration-200 hover:border-trust/60"
      >
        {file ? (
          <span className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2.5 text-sm font-semibold text-ink truncate">
              <FileImage size={18} className="text-verified shrink-0" />
              {file.name}
            </span>
            <X
              size={16}
              className="text-gray-400 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          </span>
        ) : (
          <span className="flex items-center gap-2.5 text-sm text-gray-500">
            <Upload size={18} className="text-trust shrink-0" />
            {hint}
          </span>
        )}
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </div>
  );
};

const AuthGate = ({ onAuth }) => {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState({ phase: "idle", msg: "" });

  const TITLES = {
    login: "Sign in to your reviewer account",
    register: "Create your free reviewer account",
    forgot: "Reset your password",
  };

  const submit = async (e) => {
    e.preventDefault();
    setState({ phase: "loading", msg: "" });
    try {
      if (mode === "forgot") {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setState({ phase: "success", msg: data.message });
        return;
      }
      const payload = mode === "register" ? { name, email, password } : { email, password };
      const { data } = await axios.post(`${API}/auth/${mode}`, payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuth(data.token, data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setState({ phase: "error", msg: typeof d === "string" ? d : "Could not sign you in. Try again." });
    }
  };

  return (
    <form data-testid="reviewer-auth-form" onSubmit={submit} className="mt-10 rounded-2xl bg-paper border border-black/5 p-8 sm:p-10 max-w-lg">
      <p className="flex items-center gap-2.5 font-display font-semibold text-lg">
        <KeyRound size={18} className="text-trust" />
        {TITLES[mode]}
      </p>
      <p className="mt-2 text-sm text-gray-500 leading-relaxed">
        {mode === "forgot"
          ? "Enter your account email — if it exists, a reset link lands in your inbox within a minute."
          : "Receipts are tied to real accounts — that's what makes them attributable and verifiable. No anonymous posts."}
      </p>
      <div className="mt-6 space-y-3">
        {mode === "register" && (
          <input data-testid="reviewer-name-input" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name" className={INPUT_CLASSES} />
        )}
        <input data-testid="reviewer-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com" autoComplete="email" className={INPUT_CLASSES} />
        {mode !== "forgot" && (
          <input data-testid="reviewer-password-input" required type="password" minLength={mode === "register" ? 8 : 1}
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === "register" ? "Password (8+ characters)" : "Password"}
            autoComplete={mode === "register" ? "new-password" : "current-password"} className={INPUT_CLASSES} />
        )}
      </div>
      <button data-testid="reviewer-auth-submit" type="submit" disabled={state.phase === "loading"}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-trust text-white font-semibold py-3.5 transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60">
        {state.phase === "loading" ? <Loader2 size={16} className="animate-spin" /> : mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
      </button>
      {state.msg && (
        <p data-testid={state.phase === "success" ? "reviewer-auth-success" : "reviewer-auth-error"}
          className={`mt-3 flex items-center gap-2 text-sm font-semibold ${state.phase === "success" ? "text-verified" : "text-warn"}`}>
          {state.phase === "success" ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />} {state.msg}
        </p>
      )}
      <div className="mt-4 flex items-center justify-between gap-3">
        <button type="button" data-testid="reviewer-auth-toggle"
          onClick={() => { setMode(mode === "register" ? "login" : "register"); setState({ phase: "idle", msg: "" }); }}
          className="text-sm font-semibold text-trust hover:text-trust-dark transition-colors duration-200">
          {mode === "register" ? "Already have an account? Sign in" : "New here? Create a free account"}
        </button>
        {mode !== "forgot" ? (
          <button type="button" data-testid="forgot-password-link"
            onClick={() => { setMode("forgot"); setState({ phase: "idle", msg: "" }); }}
            className="text-xs font-semibold text-gray-400 hover:text-trust transition-colors duration-200">
            Forgot password?
          </button>
        ) : (
          <button type="button" data-testid="forgot-back-to-login"
            onClick={() => { setMode("login"); setState({ phase: "idle", msg: "" }); }}
            className="text-xs font-semibold text-gray-400 hover:text-trust transition-colors duration-200">
            Back to sign in
          </button>
        )}
      </div>
    </form>
  );
};

export const ReviewForm = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    product_name: "",
    seller_name: "",
    category: "digital_product",
    price_paid: "",
    promised: "",
    received: "",
    upsell_pushed: "no",
    refund_offered: "no",
    resell_rights: "unsure",
  });
  const [paymentProof, setPaymentProof] = useState(null);
  const [productProof, setProductProof] = useState(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [myReviews, setMyReviews] = useState(null);
  const [profile, setProfile] = useState({ display_name: "", show_name: false });
  const [profileMsg, setProfileMsg] = useState("");

  const saveProfile = async () => {
    if (profile.display_name.trim().length < 2) {
      setProfileMsg("Display name needs at least 2 characters.");
      return;
    }
    try {
      await authed().patch(`${API}/my/profile`, profile);
      setProfileMsg("Saved.");
    } catch {
      setProfileMsg("Could not save — try again.");
    }
  };

  const authed = useCallback(
    () => axios.create({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const loadMine = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await authed().get(`${API}/my/reviews`);
      setMyReviews(data.reviews);
    } catch {
      setMyReviews([]);
    }
  }, [token, authed]);

  useEffect(() => {
    if (!token) return;
    authed()
      .get(`${API}/auth/me`)
      .then(({ data }) => {
        setUser(data);
        setProfile({ display_name: data.public_name || data.name || "", show_name: !!data.show_name });
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setUser(null);
      });
    loadMine();
  }, [token, authed, loadMine]);

  const onAuth = (t, u) => {
    setToken(t);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setMyReviews(null);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!paymentProof || !productProof) {
      setStatus({ state: "error", msg: "Both screenshots are required — that's the whole rule. No proof, no review." });
      return;
    }
    for (const f of [paymentProof, productProof]) {
      if (f.size > 10 * 1024 * 1024) {
        setStatus({ state: "error", msg: `"${f.name}" is over 10 MB. Trim it and try again.` });
        return;
      }
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("payment_proof", paymentProof);
      data.append("product_proof", productProof);
      const res = await authed().post(`${API}/reviews`, data);
      setStatus({ state: "success", msg: res.data.message });
      setForm({
        product_name: "", seller_name: "", category: "digital_product", price_paid: "",
        promised: "", received: "", upsell_pushed: "no", refund_offered: "no", resell_rights: "unsure",
      });
      setPaymentProof(null);
      setProductProof(null);
      loadMine();
    } catch (err) {
      const d = err.response?.data?.detail;
      setStatus({ state: "error", msg: typeof d === "string" ? d : "Upload failed — try once more?" });
    }
  };

  return (
    <section data-testid="review-form-section" className="py-24 sm:py-32 bg-white border-t border-black/5">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-verified">Founding reviewers</p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
            Burned recently? Put the receipt on record.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed">
            Describe what you paid and what you got. Attach the two proofs and it goes into verification — your
            receipt becomes the warning the next buyer needed.
          </p>

          {!token ? (
            <AuthGate onAuth={onAuth} />
          ) : (
            <>
              <div className="mt-10 flex items-center justify-between gap-4 rounded-full bg-paper border border-black/5 px-5 py-3">
                <span data-testid="reviewer-signed-in" className="flex items-center gap-2.5 text-sm font-semibold truncate">
                  <UserRound size={16} className="text-trust shrink-0" />
                  {user ? `${user.name} · ${user.email}` : "Signed in"}
                </span>
                <button data-testid="reviewer-logout-button" onClick={logout}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-warn transition-colors duration-200 shrink-0">
                  <LogOut size={13} /> Sign out
                </button>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input data-testid="review-product-name" required value={form.product_name} onChange={set("product_name")}
                    placeholder='Product name — e.g. "108 BOQ Docs Bundle"' className={INPUT_CLASSES} />
                  <input data-testid="review-seller-name" required value={form.seller_name} onChange={set("seller_name")}
                    placeholder="Seller / page / channel name" className={INPUT_CLASSES} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <select data-testid="review-category" value={form.category} onChange={set("category")} className={SELECT_CLASSES}>
                    <option value="digital_product">Digital product</option>
                    <option value="course">Course / webinar</option>
                  </select>
                  <input data-testid="review-price-paid" required value={form.price_paid} onChange={set("price_paid")}
                    placeholder="Price paid — e.g. ₹299" className={INPUT_CLASSES} />
                </div>
                <textarea data-testid="review-promised" required value={form.promised} onChange={set("promised")} rows={2}
                  placeholder='What did the ad promise? — e.g. "A software tool with walkthrough, 108 editable BOQ documents"' className={INPUT_CLASSES + " resize-none"} />
                <textarea data-testid="review-received" required value={form.received} onChange={set("received")} rows={2}
                  placeholder='What did you actually receive? — e.g. "A zip of free files scraped online, no software"' className={INPUT_CLASSES + " resize-none"} />
                <div className="grid sm:grid-cols-3 gap-5">
                  <div>
                    <p className="text-sm font-semibold mb-2">Upsell pushed?</p>
                    <select data-testid="review-upsell" value={form.upsell_pushed} onChange={set("upsell_pushed")} className={SELECT_CLASSES}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">Refund offered?</p>
                    <select data-testid="review-refund" value={form.refund_offered} onChange={set("refund_offered")} className={SELECT_CLASSES}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">Resell-rights pack?</p>
                    <select data-testid="review-resell-rights" value={form.resell_rights} onChange={set("resell_rights")} className={SELECT_CLASSES}>
                      <option value="unsure">Not sure</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <FilePicker label="Payment screenshot" hint="UPI / bank / gateway receipt" file={paymentProof} onChange={setPaymentProof} testId="review-payment-proof" />
                  <FilePicker label="Product screenshot" hint="What you actually got" file={productProof} onChange={setProductProof} testId="review-product-proof" />
                </div>

                <label className="flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer">
                  <input data-testid="review-consent-checkbox" type="checkbox" required className="mt-0.5 accent-trust w-4 h-4" />
                  <span>
                    I agree to the{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-trust font-semibold underline underline-offset-2">Privacy Policy</a>{" "}
                    and{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-trust font-semibold underline underline-offset-2">Terms of Use</a>
                  </span>
                </label>

                <button data-testid="review-submit-button" type="submit" disabled={status.state === "loading"}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-trust text-white font-semibold px-8 py-4 transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60">
                  {status.state === "loading" ? <Loader2 size={18} className="animate-spin" /> : "Submit for verification"}
                </button>

                {status.msg && (
                  <p data-testid="review-form-message" role="status"
                    className={`flex items-center gap-2 text-sm font-semibold ${status.state === "success" ? "text-verified" : "text-warn"}`}>
                    {status.state === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                    {status.msg}
                  </p>
                )}
                <p className="text-xs text-gray-400 leading-relaxed">
                  Facts only: what you paid, what you got. We never publish "fraud" labels — the receipt speaks.
                  Screenshots are stored privately and used only for verification.
                </p>
              </form>

              {myReviews && myReviews.length > 0 && (
                <div className="mt-12">
                  <h3 className="font-display font-semibold text-lg">Your receipts</h3>
                  <div data-testid="reviewer-impact-strip" className="mt-4 flex flex-wrap gap-3">
                    <span className="rounded-full bg-verified-soft text-verified px-4 py-2 text-xs font-bold uppercase tracking-wider">
                      {myReviews.filter((r) => r.status === "published").length} live
                    </span>
                    <span className="rounded-full bg-trust-soft text-trust px-4 py-2 text-xs font-bold uppercase tracking-wider">
                      Seen by searching buyers {myReviews.reduce((s, r) => s + (r.impressions || 0), 0)}×
                    </span>
                  </div>
                  {user && (
                    <div data-testid="profile-controls" className="mt-5 rounded-xl bg-white border border-black/5 p-5">
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Your public profile</p>
                      <div className="mt-3 flex flex-col sm:flex-row gap-3">
                        <input
                          data-testid="display-name-input"
                          value={profile.display_name}
                          onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))}
                          placeholder="Display name — a pseudonym is fine"
                          maxLength={60}
                          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-trust"
                        />
                        <button data-testid="profile-save-button" onClick={saveProfile}
                          className="rounded-full bg-trust text-white px-5 py-2.5 text-xs font-semibold transition-colors duration-200 hover:bg-trust-dark">
                          Save
                        </button>
                      </div>
                      <label className="mt-3 flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input type="checkbox" data-testid="show-name-toggle" checked={profile.show_name}
                          onChange={(e) => setProfile((p) => ({ ...p, show_name: e.target.checked }))}
                          className="accent-trust w-4 h-4" />
                        Show my display name on my published receipts
                      </label>
                      <p className="mt-2 text-xs text-gray-400">
                        Your email is never shown anywhere. Use any name you like — the receipts stay yours either way.
                      </p>
                      <div className="mt-3 flex items-center gap-4">
                        <a data-testid="view-profile-link" href={`/reviewer/${user.id}`} target="_blank" rel="noopener noreferrer"
                          className="text-xs font-semibold text-trust hover:text-trust-dark transition-colors duration-200 underline underline-offset-2">
                          View my public profile
                        </a>
                        <button
                          data-testid="export-data-button"
                          onClick={async () => {
                            try {
                              const { data } = await authed().get(`${API}/my/export`);
                              const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = "proofbuy-my-data.json";
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch {}
                          }}
                          className="text-xs font-semibold text-gray-500 hover:text-trust transition-colors duration-200 underline underline-offset-2"
                        >
                          Download my data
                        </button>
                        {profileMsg && <span data-testid="profile-save-msg" className="text-xs font-semibold text-verified">{profileMsg}</span>}
                      </div>
                    </div>
                  )}
                  <ul data-testid="my-receipts-list" className="mt-4 space-y-3">
                    {myReviews.map((r, i) => {
                      const meta = STATUS_META[r.status] || STATUS_META.pending_verification;
                      return (
                        <li key={r.id} data-testid={`my-receipt-${i}`}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-paper border border-black/5 px-5 py-4">
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">{r.product_name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {r.seller_name} · {r.price_paid} · {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                            </p>
                          </div>
                          <span className={`rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider ${meta.cls}`}>
                            {meta.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};
